/**
 * i18n routing helpers for Your Broker Portugal.
 *
 * URL strategy (SEO-friendly, international-standard):
 *   - English (default): /, /advisory, /services, /regions, /international, /journal, /contact
 *   - Portuguese:        /pt, /pt/advisory, /pt/services, /pt/regions, /pt/international, /pt/journal, /pt/contact
 *
 * Slugs are kept in English in both languages. Google differentiates the locales
 * via <link rel="alternate" hreflang="..."> emitted by the SEO component.
 */

export const DEFAULT_LANGUAGE = 'en';
export const LANGUAGES = ['en', 'pt'];

// Canonical slug map: every page in the site
// (used to build hreflang alternates and to validate routes)
export const PAGES = [
  { slug: '/', label: { en: 'Home', pt: 'Início' } },
  { slug: '/advisory', label: { en: 'Advisory', pt: 'Consultoria' } },
  { slug: '/services', label: { en: 'Services', pt: 'Serviços' } },
  { slug: '/regions', label: { en: 'Regions', pt: 'Regiões' } },
  { slug: '/international', label: { en: 'International', pt: 'International' } },
  { slug: '/journal', label: { en: 'Journal', pt: 'Journal' } },
  { slug: '/contact', label: { en: 'Contact', pt: 'Contacto' } },
];

/**
 * Derives the active language from a URL pathname.
 *   /pt           -> 'pt'
 *   /pt/anything  -> 'pt'
 *   /             -> 'en'
 *   /advisory     -> 'en'
 */
export const getLangFromPath = (pathname = '/') => {
  if (pathname === '/pt' || pathname.startsWith('/pt/') || pathname.startsWith('/pt?')) {
    return 'pt';
  }
  return 'en';
};

/**
 * Strips the language prefix from a pathname.
 *   /pt/advisory -> /advisory
 *   /advisory    -> /advisory
 *   /pt          -> /
 *   /            -> /
 */
export const stripLangPrefix = (pathname = '/') => {
  if (pathname === '/pt') return '/';
  if (pathname.startsWith('/pt/')) {
    const rest = pathname.slice(3); // remove '/pt'
    return rest || '/';
  }
  return pathname;
};

/**
 * Builds a localized path for a given canonical slug.
 *   buildLocalizedPath('en', '/advisory') -> '/advisory'
 *   buildLocalizedPath('pt', '/advisory') -> '/pt/advisory'
 *   buildLocalizedPath('pt', '/')         -> '/pt'
 */
export const buildLocalizedPath = (lang, path = '/') => {
  // Normalize: ensure leading slash, no trailing slash (except root)
  let p = path.startsWith('/') ? path : `/${path}`;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);

  if (lang === 'en') return p;
  if (p === '/') return '/pt';
  return `/pt${p}`;
};

/**
 * Given the current pathname, returns the equivalent pathname in the other language.
 *   /advisory     -> /pt/advisory
 *   /pt/advisory  -> /advisory
 */
export const swapLangInPath = (pathname = '/') => {
  const current = getLangFromPath(pathname);
  const bare = stripLangPrefix(pathname);
  const next = current === 'en' ? 'pt' : 'en';
  return buildLocalizedPath(next, bare);
};
